import React, { useState, useEffect, useMemo } from 'react';
import { Container, Button, Form, Row, Badge, Alert } from 'react-bootstrap';
import { 
  availableCards, 
  cardLookup, 
  CARD_TYPES,
  CARD_RARITY,
  DECK_CONSTRAINTS,
  validateDeck,
  getDeckWarnings,
  deckCardStyle,
} from '../CardData';

const DeckBuilder = ({ initialDeck = [], initialDeckName = '', onSave, onCancel }) => {
  // Initialize deck with IDs
  const initialDeckIds = Array.isArray(initialDeck) ? 
	initialDeck.map(card => typeof card === 'string' ? card : card.id) : 
	[];

  // State
  const [deckName, setDeckName] = useState(initialDeckName || 'Deck 1');
  const [deck, setDeck] = useState(initialDeckIds);
  const [filterType, setFilterType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState([]);

  // Computed values
  const cardCounts = useMemo(() => {
	const counts = new Map();
	deck.forEach(cardId => {
	  counts.set(cardId, (counts.get(cardId) || 0) + 1);
	});
	return counts;
  }, [deck]);

  const deckStats = useMemo(() => {
	const cards = deck.map(id => cardLookup.get(id)).filter(Boolean);
	return {
	  total: cards.length,
	  types: {
		[CARD_TYPES.WISDOM]: cards.filter(card => card.type === CARD_TYPES.WISDOM).length,
		[CARD_TYPES.ECO]: cards.filter(card => card.type === CARD_TYPES.ECO).length,
		[CARD_TYPES.TECH]: cards.filter(card => card.type === CARD_TYPES.TECH).length
	  },
	  energy: cards.reduce((sum, card) => sum + (card.energy || 0), 0)
	};
  }, [deck]);

  const filteredCards = useMemo(() => {
	return availableCards
	  .filter(card => {
		const matchesType = filterType === 'All' || card.type === filterType;
		const matchesSearch = card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
							card.description.toLowerCase().includes(searchTerm.toLowerCase());
		return matchesType && matchesSearch;
	  })
	  .sort((a, b) => {
		if (a.type !== b.type) return a.type.localeCompare(b.type);
		if (a.rarity !== b.rarity) return a.rarity === CARD_RARITY.SPECIAL ? -1 : 1;
		return a.name.localeCompare(b.name);
	  });
  }, [filterType, searchTerm]);

  // Validation
  useEffect(() => {
	const warnings = getDeckWarnings(deck);
	setErrors(warnings);
  }, [deck]);

  // Card management
  const addCard = (cardId) => {
	const card = cardLookup.get(cardId);
	if (!card) return;

	const count = cardCounts.get(cardId) || 0;
	const maxCopies = card.rarity === CARD_RARITY.SPECIAL ? 1 : DECK_CONSTRAINTS.MAX_COPIES;

	if (count >= maxCopies || deck.length >= DECK_CONSTRAINTS.MAX_CARDS) return;

	setDeck(prev => [...prev, cardId]);
  };

  const removeCard = (cardId) => {
	const index = deck.lastIndexOf(cardId);
	if (index === -1) return;
	setDeck(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const handleSave = () => {
	if (!deckName?.trim()) {
	  setErrors(prev => [...prev, 'Please enter a deck name']);
	  return;
	}

	if (deckName.trim() === 'Starter Deck') {
	  setErrors(prev => [...prev, 'Cannot save as "Starter Deck"']);
	  return;
	}

	const validation = validateDeck(deck);
	if (!validation.valid) {
	  setErrors(prev => [...prev, validation.error]);
	  return;
	}

	const fullDeck = deck.map(id => {
	  const card = cardLookup.get(id);
	  if (!card) {
		console.error(`Invalid card ID: ${id}`);
		return null;
	  }
	  return { ...card };
	}).filter(Boolean);

	if (fullDeck.length !== deck.length) {
	  setErrors(prev => [...prev, 'Some cards in deck are invalid']);
	  return;
	}

	onSave(deckName.trim(), fullDeck);
  };

  const renderCard = (card) => {
	const count = cardCounts.get(card.id) || 0;
	const maxCopies = card.rarity === CARD_RARITY.SPECIAL ? 1 : DECK_CONSTRAINTS.MAX_COPIES;

	return (
	  <div 
		key={card.id}
		style={{
		  ...deckCardStyle,
		}}
		
	  >
		<div style={{
		
		}}>
		  <img
			src={card.imageUrl}
			alt={card.name} className="card-image"
		  />
		  <div className="card-info">
			<div style={{ fontWeight: 'bold' }}>{card.name}</div>
			  <Badge bg={
				card.type === 'Eco' ? 'success' :
				card.type === 'Tech' ? 'secondary' : 'info'
			  }>
				{card.type}
			  </Badge>
			<div>{card.description}</div>
			{card.energy !== 0 && (
			  <div className={`energy-value ${card.energy > 0 ? 'text-success' : 'text-danger'}`}
				   style={{ fontWeight: '500' }}>
				{card.energy > 0 ? '+' : ''}{card.energy} energy
			  </div>
			)}
		  </div>
		</div>

		

		<div style={{
		  padding: '5px',
		  marginTop: 'auto',
		  display: 'flex',
		  justifyContent: 'space-between',
		  alignItems: 'center',
		}}>
		  <Button
			size="sm"
			variant={count > 0 ? "outline-danger" : "outline-secondary"}
			onClick={(e) => {
			  e.stopPropagation();
			  removeCard(card.id);
			}}
			disabled={count === 0}
			style={{ width: '40px', margin: '5px' }}
		  >
			-
		  </Button>
		  <span style={{ 
			fontSize: '0.9rem', 
			fontWeight: '500',
			color: count >= maxCopies ? '#dc3545' : '#212529'
		  }}>
			{count}/{maxCopies}
		  </span>
		  <Button
			size="sm"
			variant={count < maxCopies && deck.length < DECK_CONSTRAINTS.MAX_CARDS ? 
			  "outline-success" : "outline-secondary"}
			onClick={(e) => {
			  e.stopPropagation();
			  addCard(card.id);
			}}
			disabled={count >= maxCopies || deck.length >= DECK_CONSTRAINTS.MAX_CARDS}
			style={{ width: '40px', margin: '5px' }}
		  >
			+
		  </Button>
		</div>
	  </div>
	);
  };

  return (
	<div className="page-transition" style={{ 
	 paddingTop: '50px',
	 paddingBottom: '100px',
	}}>
		<div>
		  <Container>
			<div className="d-flex justify-content-between align-items-center" >
			  <Button 
				variant="light" 
				onClick={onCancel}
				className="d-flex align-items-center gap-2"
			  >
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
				  <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
				</svg>
				Back
			  </Button>
			  <Form.Control
				type="text"
				placeholder="Deck Name"
				value={deckName}
				onChange={(e) => setDeckName(e.target.value)}
				style={{ maxWidth: '300px', marginLeft: '10px' }}
			  />
			</div>
		  </Container>
		</div>

		{/* Main content */}
		<Container style={{ marginTop: '10px' }}>
		  {errors.length > 0 && (
			<Alert variant="warning" className="mb-3">
			  <ul className="mb-0">
				{errors.map((error, index) => (
				  <li key={index}>{error}</li>
				))}
			  </ul>
			</Alert>
		  )}

		  <div className="mb-3">
			<div className="d-flex gap-2 flex-wrap">
			  <Badge bg="info">Wisdom: {deckStats.types[CARD_TYPES.WISDOM]}</Badge>
			  <Badge bg="success">Eco: {deckStats.types[CARD_TYPES.ECO]}</Badge>
			  <Badge bg="secondary">Tech: {deckStats.types[CARD_TYPES.TECH]}</Badge>
			</div>
		  </div>

		  <div className="mb-3">
			<div className="d-flex gap-2 mb-2 flex-wrap">
			  {['All', ...Object.values(CARD_TYPES)].map(type => (
				<Button
				  key={type}
				  variant={filterType === type ? 'light' : 'outline-light'}
				  onClick={() => setFilterType(type)}
				  size="sm"
				>
				  {type}
				</Button>
			  ))}
			</div>
			<Form.Control
			  type="text"
			  placeholder="Search cards..."
			  value={searchTerm}
			  onChange={(e) => setSearchTerm(e.target.value)}
			/>
		  </div>

		  <Row>
			<div style={{display: 'flex',
				flexDirection: 'row',
				flexWrap: 'wrap',}}>
			  <div style={{ 
				
			  }}>
				{filteredCards.map(card => renderCard(card))}
			  </div>
			</div>
			
		  </Row>
		</Container>

		{/* Fixed bottom bar */}
		<div style={{
		  position: 'fixed',
		  bottom: 0,
		  left: 0,
		  right: 0,
		  backgroundColor: 'rgba(255, 255, 255, 0.95)',
		  backdropFilter: 'blur(10px)',
		  borderTop: '1px solid #ddd',
		  padding: '30px',
		  zIndex: 1000,
		  boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
		}}>
		  <Container>
			<div className="d-flex justify-content-between align-items-center">
			  <div>
				<Badge bg={deckStats.total < DECK_CONSTRAINTS.MIN_CARDS ? 'danger' : 
							deckStats.total > DECK_CONSTRAINTS.MAX_CARDS ? 'danger' : 'success'}>
					Cards: {deckStats.total}/{DECK_CONSTRAINTS.MAX_CARDS}
				  </Badge>
							   </div>
							   <div className="d-flex gap-2">
								 <Button 
								   variant="secondary" 
								   onClick={() => {
									 if (deck.length > 0) {
									   if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
										 onCancel();
									   }
									 } else {
									   onCancel();
									 }
								   }}
								 >
								   Cancel
								 </Button>
								 <Button 
								   variant="success" 
								   onClick={handleSave}
								   disabled={
									 !deckName?.trim() || 
									 deckStats.total < DECK_CONSTRAINTS.MIN_CARDS ||
									 deckStats.total > DECK_CONSTRAINTS.MAX_CARDS ||
									 errors.length > 0
								   }
								 >
								   Save
								 </Button>
							   </div>
							 </div>
						   </Container>
						 </div>
					   </div>
				 
					 
				   );
				 };
				 
				 export default DeckBuilder;